import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Subscription, of } from 'rxjs';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { Router, ActivatedRoute, ParamMap } from '@angular/router';

import { ClientService } from '../../../services/client/client.service';
import { AuthenticationService } from '../../../services/authentication/authentication.service';
import { AuthorizationService } from '../../../services/authorization/authorization.service';
import { ScreenService } from '../../../services/screen/screen.service';
import { LocationService } from '../../../services/location/location.service';
import { SupportService } from '../../../services/support/support.service';
import { SettingsService } from '../../../services/settings/settings.service';
import { UserSettingsService, ThemeKey } from '../../../services/user-settings/user-settings.service';
import { HeaderService, HeaderBackLink } from '../../../services/header/header.service';
import packageJson from '../../../../../package.json';

// import { MessagingComponent } from '../messaging/messaging.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {

  clientConfig;

  activeScreen;
  screens;

  activeLocation;
  locations;
  locationSearchText;

  screensMenuOpen;
  locationsMenuOpen;
  userMenuOpen;

  user;

  canCommunicate;
  messagingRef;
  messageCount;
  summaryUpdated: Subscription;

  activeTheme: ThemeKey = 'light';
  themes: { key: ThemeKey, label: string }[] = [
    { key: 'light', label: 'Light' },
    { key: 'dark', label: 'Dark' },
    { key: 'blueIce', label: 'Blue Ice' },
    { key: 'redFire', label: 'Red Fire' },
  ];

  public version: string = packageJson.version;

  @ViewChild('locationSearch') locationSearch: ElementRef;

  private activeScreenSet: Subscription;
  // private screensUpdated: Subscription;
  private permissionsLoaded: Subscription;

  // Screen-provided "back to X" button (HeaderService), e.g. a transfer's
  // link to the report listing its type.
  backLink: HeaderBackLink = null;
  private backLinkChanged: Subscription;
  private locationsMenuRequested: Subscription;

  constructor(
    private clientService: ClientService,
    private authenticationService: AuthenticationService,
    private authorizationService: AuthorizationService,
    private screenService: ScreenService,
    private supportService: SupportService,
    private settingsService: SettingsService,
    private locationService: LocationService,
    private userSettingsService: UserSettingsService,
    private headerService: HeaderService,
    private router: Router,
    private route: ActivatedRoute,
    public dialog: MatDialog,
  ) {}

  ngOnInit() {
    console.log('Header INIT');
    this.clientConfig = this.clientService.getConfig();

    if (this.clientConfig.logoUrl) {
      console.log('UPDATE LOGO SOURCE');
      this.clientConfig.logoUrl = this.clientConfig.logoUrl.replace("white", "black");
    }

    this.activeScreen = this.screenService.getActiveScreen();
    this.screens = this.screenService.getScreens();

    let locations = this.locationService.getLocations();
    if (locations) {
      this.locations = locations.filter(location => this.locationService.isSelectable(location));

      console.log('locations: ', this.locations);
    }

    this.locationSearchText = this.locationService.getSearchText();

    this.locationService.locationsSetObservable().subscribe(locations => {
      if (locations) {
        // console.log('HEADER LOCATIONS UPDATE: ', locations);
        this.locations = locations.filter(location => this.locationService.isSelectable(location));
      } else {
        this.locations = [];
      }
    });

    this.activeLocation = this.locationService.getActiveLocation();
    // this.messageService.monitorSummary(true);  // TODO: Re-enable conditionally

    this.locationService.activeLocationSetObservable().subscribe(location => {
      this.activeLocation = location;
    });

    // Subscribe to activeScreen changes
    this.activeScreenSet = this.screenService.activeScreenSetObservable().subscribe(activeScreen => {
      this.activeScreen = activeScreen;
    });

    // Subscribe to screens changes
    // this.screensUpdated = this.screenService.screensUpdatedObservable().subscribe(screens => {
    //   this.updateScreens(screens);
    // });

    // Update user info when permissions are loaded
    this.permissionsLoaded = this.authorizationService.permissionsLoadedObservable().subscribe(() => {
      this.permissionsAreLoaded();
    });

    if (this.authorizationService.permissionsAreLoaded()) {
      this.permissionsAreLoaded();
    }

    // If the user is already available, set it here
    this.user = this.authorizationService.getUser();

    this.activeTheme = this.userSettingsService.getTheme();
    this.userSettingsService.themeChangedObservable().subscribe(theme => {
      this.activeTheme = theme;
    });

    this.backLink = this.headerService.getBackLink();
    this.locationsMenuRequested = this.headerService.locationsMenuRequestedObservable().subscribe(() => {
      if (!this.locationsMenuOpen) {
        this.toggleLocationsMenu();
      }
    });
    this.backLinkChanged = this.headerService.backLinkObservable().subscribe(link => {
      this.backLink = link;
    });
  }

  ngOnDestroy() {
    this.activeScreenSet.unsubscribe();
    // this.screensUpdated.unsubscribe();
    this.permissionsLoaded.unsubscribe();
    if (this.locationsMenuRequested) {
      this.locationsMenuRequested.unsubscribe();
    }
    if (this.backLinkChanged) {
      this.backLinkChanged.unsubscribe();
    }

    if (this.summaryUpdated) {
      this.summaryUpdated.unsubscribe();
    }
  }

  goToDashboard() {
    this.router.navigate(['']);
  }

  goBack() {
    if (this.backLink) {
      this.backLink.action();
    }
  }

  permissionsAreLoaded() {
    this.user = this.authorizationService.getUser();
  }

  updateScreens(screens) {
    this.screens = screens;
  }

  setActiveLocation(location) {
    this.locationService.setActiveLocation(location);
    // The search has served its purpose once a location is picked — reset it
    // so the menu shows the full list next time it opens.
    this.locationSearchText = null;
    this.locationService.setSearchText(null);
  }

  searchClick(event) {
    event.stopPropagation();
  }

  searchLocations(event: any) {
    this.locationSearchText = event.target.value;
    this.locationService.setSearchText(this.locationSearchText);
  }

  clearSearch() {
    this.locationSearchText = null;
    this.locationService.setSearchText(this.locationSearchText);
    this.locationSearch.nativeElement.focus();
  }

  toggleScreensMenu() {
    this.locationsMenuOpen = false;
    this.userMenuOpen = false;
    this.screensMenuOpen = !this.screensMenuOpen;
  }

  toggleLocationsMenu() {
    this.screensMenuOpen = false;
    this.userMenuOpen = false;
    this.locationsMenuOpen = !this.locationsMenuOpen;
    if (this.locationsMenuOpen && this.locationSearch) {
      setTimeout(() => {
        this.locationSearch.nativeElement.focus();
      }, 0);
    }
  }

  toggleUserMenu() {
    this.screensMenuOpen = false;
    this.locationsMenuOpen = false;
    this.userMenuOpen = !this.userMenuOpen;
  }

  setTheme(theme: ThemeKey) {
    this.userSettingsService.setTheme(theme);
  }

  openSettings() {
    this.userMenuOpen = false;
    this.settingsService.launchSettings();
  }

  // A PIN-unlocked session on an authorized device "locks" rather than signs
  // out: the session ends but the device stays authorized, so the PIN pad
  // is what comes back.
  isDeviceSession(): boolean {
    return this.authenticationService.isDeviceSession();
  }

  signOut() {
    this.authenticationService.signout();
  }

  // Support lives in the user menu (with Settings) rather than as its own
  // header button, so the header stays compact on narrow screens.
  launchSupport() {
    this.userMenuOpen = false;
    this.supportService.launchSupport();
  }
}
